import React, { useRef, useEffect, memo } from 'react'
import { useFeatureFlags } from '@pff-consumer/feature-flags'
import { SubscriptionPlans } from '@pff-consumer/schema'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/browser-text'
import { colors } from '@pff-consumer/core-ui'

import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { Icon } from '@pff-consumer/tweed-ui/atoms/icon/icon.web'
import { isMonthlyPlan, SVGGroup } from '@pff-consumer/utils'
import { SubscriptionPlanTileProps } from './subscription-plan-tile.props'
import { useStyles } from './subscription-plan-tile.styles'

export const SubscriptionPlanTile = memo((props: SubscriptionPlanTileProps) => {
  const { type, currency, price, description, description2, plan, planStyle, handlePlanClick } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const isTileSelected = type === plan
  const styles = useStyles()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { getFeatureFlag } = useFeatureFlags()
  const annualHeadline = getFeatureFlag<string>('subscribe-annual-headline', 'Save $179')
  const planName = isMonthlyPlan(type) ? `Monthly` : `Annual`

  useEffect(() => {
    inputRef?.current?.focus()
  }, [])

  const handleClick = () => {
    handlePlanClick(type)
    sendSubscribeAnalyticsEvent('Subscription Plan Selected', {
      subscriptionPlanType: type === SubscriptionPlans.PREMIUM_ANNUAL ? 'Annual' : 'Monthly',
      experience: 'Single Page Checkout',
      price: parseFloat(price),
      isTrialIncluded: type === SubscriptionPlans.PREMIUM_ANNUAL,
    })
  }

  return (
    <label className={planStyle}>
      <div className={styles.container}>
        {type === SubscriptionPlans.PREMIUM_ANNUAL ? (
          <div className={styles.trial}>
            <Text
              colorOverride={colors.webInverseTextColor}
              textAlign='center'
              variant={TextDescriptionVariant.SMALL_SECONDARY_SEMIBOLD}
            >
              {annualHeadline}
            </Text>
          </div>
        ) : null}

        <div className={styles.plan}>
          <Icon
            name={isTileSelected ? 'radio-check-blue' : 'radio-unchecked'}
            group={SVGGroup.Icons}
            width={16}
            height={16}
          />

          <div>
            <Text
              colorOverride={isTileSelected ? colors.webInverseTextColor : colors.webFadedTextColor}
              variant={TextDescriptionVariant.MEDIUM_PRIMARY_SEMIBOLD}
            >
              {planName}
            </Text>

            <input
              type='radio'
              name='subscriptionPlan'
              value={type}
              checked={type === plan}
              className={styles.radioButton}
              onClick={handleClick}
              data-testid={type}
              readOnly
              data-analytics-id={`plan-selector-${type}`}
              ref={inputRef}
            />
          </div>
        </div>

        <Text
          colorOverride={isTileSelected ? colors.webInverseTextColor : colors.webFadedTextColor}
          variant={TextDescriptionVariant.XXLARGE_PRIMARY_BOLD}
        >
          {currency}
          {price}
        </Text>
        <Text
          colorOverride={isTileSelected ? colors.webInverseTextColor : colors.webFadedTextColor}
          variant={TextDescriptionVariant.SMALL_PRIMARY_REGULAR}
        >
          {description}
        </Text>
        {description2 !== '' ? (
          <Text
            colorOverride={isTileSelected ? colors.webInverseTextColor : colors.webFadedTextColor}
            variant={TextDescriptionVariant.SMALL_PRIMARY_REGULAR}
          >
            {description2}
          </Text>
        ) : null}
      </div>
    </label>
  )
})
